import { useCallback, useState } from 'react'
import type { Phase } from '../useSession'
import { Petals } from './Petals'
import { WanderingDog } from './WanderingDog'
import type { Direction } from './WanderingDog'
import { WanderingTerrier } from './WanderingTerrier'

/**
 * The ambient background layer, rendered behind all content.
 *
 * This is the seam for "fun things happening in the background". It sits fixed
 * behind everything with pointer events off, and it knows the current phase so
 * it can behave differently before and after a guest is through the code prompt.
 *
 * Right now it drifts petals once the invite is open. Replace or add to it
 * freely; the only constraints are that it must never capture pointer events and
 * must stay quiet under prefers-reduced-motion (handled in animations.css).
 */
export function AnimationStage({ phase }: { phase: Phase }) {
  const open = phase === 'unlocking' || phase === 'unlocked'

  // The terrier comes on behind the retriever, heading the same way. A counter
  // rather than a boolean, so two cameos the same way still read as two cues.
  const [cue, setCue] = useState<{ id: number; heading: Direction } | null>(null)
  const followOn = useCallback(
    (heading: Direction) => setCue((last) => ({ id: (last?.id ?? 0) + 1, heading })),
    [],
  )

  return (
    <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden" aria-hidden="true">
      {/* A soft wash that warms up once the guest is in. */}
      <div
        className={`absolute inset-0 transition-opacity duration-[2500ms] ${
          open ? 'opacity-100' : 'opacity-0'
        }`}
        style={{
          background:
            'radial-gradient(60rem 40rem at 15% 10%, rgba(214,166,128,0.20), transparent 65%),' +
            'radial-gradient(50rem 36rem at 85% 85%, rgba(157,75,82,0.16), transparent 65%)',
        }}
      />

      {open && <Petals />}
      {open && <WanderingDog onSetOff={followOn} />}
      {open && <WanderingTerrier cue={cue} />}
    </div>
  )
}
